import { useContext, useEffect, useRef, useState } from "react";
import {
  Grid,
  TextField,
  Divider,
  IconButton,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Button,
  Card,
  CardHeader,
  CardContent,
} from "@mui/material";
import {
  CloudUpload,
  NoteAddOutlined,
  ExpandMore,
  Send,
  Visibility,
  NotificationAdd,
} from "@mui/icons-material";
import DatePicker from "../../../../Components/Common/Inputs/DatePicker";
import { CustomButtonPlusIcon } from "../../../../Components/Common/Design/CustomButtonPlusIcon";
import dayjs from "dayjs";
import Swal from "sweetalert2";
import {
  buildMessageHistory,
  encodeS3Path,
} from "../../../../Components/helpers/Helpers";
import api from "../../../../api/axios";
import {
  DocsContext,
  PermissionContext,
} from "../../../../Components/Context/Context";
dayjs.locale("es"); // establece español como idioma por defecto

const Notification = ({
  dataCase,
  activeTask,
  nextTask,
  activeFlow,
  caseId,
  userLogged,
  setUpdatedCase,
  updatedCase,
  docsChangedFlag,
  setDocsChangedFlag,
}) => {
  const { can } = useContext(PermissionContext);
  const { docsLoading, setDocsLoading } = useContext(DocsContext);
  const docsLoadingRef = useRef(docsLoading);

  useEffect(() => {
    docsLoadingRef.current = docsLoading;
  }, [docsLoading]);

  const [receptionDate, setReceptionDate] = useState(dayjs());
  const [message, setMessage] = useState(activeTask?.message || "");
  const [notifications, setNotifications] = useState(null);
  const [notifCollapsed, setNotifCollapse] = useState(false);
  const [notificationDocumentName, setNotificationDocumentName] = useState(
    "Notificación de " + activeFlow?.status_name,
  );
  const [uploadedFiles, setUploadedFiles] = useState([]);

  const waitForDocsRefresh = async (timeoutMs = 25000) => {
    const start = Date.now();

    while (docsLoadingRef.current) {
      if (Date.now() - start > timeoutMs) break;
      // Small delay to wait for DocsProvider + ArchivosMostrar refresh cycle.
      await new Promise((resolve) => setTimeout(resolve, 120));
    }
  };

  const handleNotification = async () => {
    if (
      notificationDocumentName !== "" &&
      receptionDate &&
      uploadedFiles.length > 0
    ) {
      try {
        // 🟡 CONFIRMACIÓN
        const confirm = await Swal.fire({
          title: "¿Confirmar notificación?",
          text: "Se subirán los archivos y se registrará la notificación",
          icon: "question",
          showCancelButton: true,
          confirmButtonText: "Sí, guardar",
          cancelButtonText: "Cancelar",
          confirmButtonColor: "#ed6c02",
          cancelButtonColor: "#d33",
          reverseButtons: true,
        });

        if (!confirm.isConfirmed) return;

        // 🔵 LOADING
        Swal.fire({
          title: "Procesando...",
          text: "Subiendo archivos y actualizando tarea",
          allowOutsideClick: false,
          didOpen: () => {
            Swal.showLoading();
          },
        });

        const formData = new FormData();
        formData.append("case_id", Number(caseId));
        formData.append("user_id", userLogged.id);
        formData.append("folder", "Sin Carpeta");
        formData.append("flag_name", true);
        formData.append(
          "doc_desc",
          "Notificación recibida el " +
            dayjs(receptionDate).format("DD/MM/YYYY HH:mm"),
        );

        uploadedFiles.forEach((file) => {
          const ext = file.name.split(".").pop();
          const newName = `${notificationDocumentName}.${ext}`;
          const renamedFile = new File([file], newName, {
            type: file.type,
          });

          formData.append("files[]", renamedFile);
        });

        const urlUpload = `${process.env.REACT_APP_API_URL}/s3/store`;

        const response = await api.post(urlUpload, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });

        if (!response?.data || !response?.data?.paths?.[0]) {
          throw new Error("Error en la subida de archivos");
        }

        const filePath = response.data.paths[0];

        const config = {
          ...(activeTask?.config ? activeTask.config : {}),
          notification: {
            date: receptionDate,
            file: filePath,
          },
        };

        const newMessage =
          `<div class="feed-element"><div><span class="label label-warning pull-right">${dayjs().format(
            "DD/MM/YYYY HH:mm",
          )}</span><hr><strong>Notificación recibida el ${dayjs(
            receptionDate,
          ).format("DD/MM/YYYY HH:mm")}</strong></div></div>`.replace(
            /\n\s*/g,
            "",
          );

        let fullMessage = buildMessageHistory(activeTask?.message, newMessage);

        const formDataTask = new FormData();
        formDataTask.append("current_task", activeTask?.id);
        formDataTask.append("config", JSON.stringify(config));
        formDataTask.append("message", fullMessage);

        const urlUpdate = `${process.env.REACT_APP_API_URL}/casestasks/updatetasks`;
        await api.post(urlUpdate, formDataTask);

        Swal.fire({
          title: "Actualizando documentos...",
          html: "Se estan cargando los nuevos archivos en pantalla.",
          allowOutsideClick: false,
          allowEscapeKey: false,
          showConfirmButton: false,
          didOpen: () => {
            Swal.showLoading();
          },
        });

        docsLoadingRef.current = true;
        setDocsLoading(true);
        setDocsChangedFlag((prev) => !prev);

        await waitForDocsRefresh();

        Swal.close();
        Swal.fire({
          icon: "success",
          title: "Notificación guardada",
          text: "La información fue registrada correctamente",
          confirmButtonColor: "#ed6c02",
        });

        // 🧼 RESET ESTADO
        setUploadedFiles([]);
        setMessage("");
        setUpdatedCase(!updatedCase);
      } catch (error) {
        setDocsLoading(false);
        console.error(error);

        //  ERROR
        Swal.fire({
          icon: "error",
          title: "Error en la operación",
          text:
            error?.response?.data?.message ||
            error.message ||
            "Ocurrió un error inesperado",
          confirmButtonColor: "#d33",
        });
      }
    } else {
      Swal.fire({
        title: "Datos incompletos",
        text: "Debe completar fecha, nombre y archivo antes de continuar",
        icon: "warning",
        confirmButtonColor: "#ed6c02",
        confirmButtonText: "Volver",
      });
    }
  };

  useEffect(() => {
    setMessage(activeTask?.message || "");
  }, [activeTask]);

  useEffect(() => {
    try {
      let notif =
        activeTask?.config?.notification &&
        Object.keys(activeTask.config.notification).length > 0
          ? activeTask.config.notification
          : null;

      setNotifications(notif);
      setNotifCollapse(notif ? true : false);
    } catch (e) {
      console.error("Error parsing config", e);
    }
  }, [activeTask]);

  return (
    <Grid
      container
      item
      xl={5.8}
      lg={5.5}
      md={12}
      p={1}
      sx={{ display: "block" }}
    >
      <Grid item xs={12}>
        <Alert
          severity="warning"
          icon={<NotificationAdd />}
          sx={{ fontWeight: "700", height: "45px" }}
        >
          NOTIFICACIÓN
        </Alert>
      </Grid>

      {/* Panel ya cargado */}
      {notifications && (
        <Grid container>
          <Card
            sx={{
              width: "100%",
              background: "#fff7f2",
              border: "1px solid #ed6c0230",
              borderRadius: "10px",
              boxShadow: 3,
              margin: "10px",
            }}
          >
            <CardHeader
              avatar={<Send sx={{ color: "#ed6c02" }} />}
              title={
                <div
                  style={{
                    fontWeight: 600,
                    display: "flex",
                    justifyContent: "space-between",
                  }}
                >
                  <Typography variant="body2">Última notificación</Typography>

                  <Typography variant="body2">
                    {notifications?.date
                      ? dayjs(notifications?.date).format(
                          "DD/MM/YYYY HH:mm",
                        ) !== "Invalid Date"
                        ? dayjs(notifications?.date).format("DD/MM/YYYY HH:mm")
                        : notifications?.date
                      : ""}
                  </Typography>
                </div>
              }
              sx={{ backgroundColor: "#ed6c0231" }}
            />
            <CardContent sx={{ display: "flex", alignItems: "center" }}>
              <Typography variant="body2">
                La <strong>notificación {activeFlow?.status_name}</strong> fue
                recibida el{" "}
                <strong>
                  {notifications?.date
                    ? dayjs(notifications?.date).format("DD/MM/YYYY HH:mm") !==
                      "Invalid Date"
                      ? dayjs(notifications?.date).format("DD/MM/YYYY HH:mm")
                      : notifications?.date
                    : ""}
                </strong>
                .
              </Typography>

              {notifications?.file && (
                <IconButton
                  component="a"
                  href={`${process.env.REACT_APP_S3_API_URL}/${encodeS3Path(
                    notifications.file,
                  )}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  color="warning"
                  size="small"
                >
                  <Visibility color="warning" />
                </IconButton>
              )}
            </CardContent>
          </Card>
        </Grid>
      )}

      {/* Accordion */}
      {can("cases", "docs") && (
        <Accordion
          expanded={!notifCollapsed}
          onChange={() => setNotifCollapse(!notifCollapsed)}
        >
          <AccordionSummary
            expandIcon={<ExpandMore />}
            sx={{
              background: "#ed6c02",
              textAlign: "center",
              color: "white",
              fontWeight: "700",
              maxHeight: "38px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              minHeight: "38px",
              width: "100%",
              "&.Mui-expanded": {
                minHeight: "38px",
                background: "#c75a02",
              },
              "& .MuiAccordionSummary-content": {
                margin: 0,
              },
            }}
          >
            ADJUNTAR NOTIFICACIÓN
          </AccordionSummary>
          <AccordionDetails>
            <Grid
              container
              sx={{
                "& > *": { padding: "10px" },
                background: "#fff9f4",
                border: "1px dashed #fff3e8",
              }}
            >
              <Grid item xs={12}>
                <b>
                  Adjunta la notificación recibida de los organismos oficiales.
                </b>
              </Grid>
              <Grid
                container
                sx={{
                  alignContent: "flex-start",
                  display: "flex",
                  gap: "10px",
                }}
              >
                <Grid item xs={12}>
                  <DatePicker
                    color={"warning"}
                    text="Fecha de recepción"
                    date={receptionDate}
                    onChange={(newValue) => {
                      setReceptionDate(newValue);
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    sx={{ fontSize: "14px" }}
                    label="Notificación informes médicos actualizados"
                    variant="outlined"
                    value={notificationDocumentName}
                    onChange={(e) =>
                      setNotificationDocumentName(e.target.value)
                    }
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button
                    variant="outlined"
                    component="label"
                    startIcon={<CloudUpload />}
                    color="warning"
                    fullWidth
                  >
                    Cargar archivo
                    <input
                      type="file"
                      hidden
                      onChange={(event) => {
                        const files = Array.from(event.target.files);
                        setUploadedFiles((prev) => [...prev, ...files]);
                      }}
                    />
                  </Button>

                  {uploadedFiles.length > 0 ? (
                    uploadedFiles.map((file, index) => (
                      <Typography
                        key={index}
                        variant="body2"
                        sx={{ marginTop: "10px", textAlign: "center" }}
                      >
                        {file.name}
                      </Typography>
                    ))
                  ) : (
                    <Typography
                      variant="body2"
                      sx={{
                        color: "red",
                        marginTop: "8px",
                        textAlign: "center",
                      }}
                    >
                      No se ha seleccionado un documento
                    </Typography>
                  )}
                </Grid>
                <Grid item xs={12} py={"10px"}>
                  <Divider width={"100%"} />
                </Grid>
                <Grid item xs={12} sx={{ textAlign: "end" }}>
                  <CustomButtonPlusIcon
                    handleFunction={handleNotification}
                    text={"Adjuntar notificación"}
                    icon={NoteAddOutlined}
                    position={"start"}
                    variant={"contained"}
                    color={"warning"}
                    size={"small"}
                  />
                </Grid>
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>
      )}
    </Grid>
  );
};

export default Notification;
